import {
  Component,
  h,
  Prop,
  State,
  Element,
  Listen,
  Host,
  Method,
} from "@stencil/core"
import {
  menuFlyoutReducer,
  getInitialState,
  MenuFlyoutState,
  MenuFlyoutActions,
} from "../../menu-flyout-store"
import {
  StoreConnection,
  StateHandle,
  computedProperty,
  getAppearance,
} from "../../../../core/helpers/webcomponent-helpers"
import { IconName } from "../../../../core/types/icon-names"

@Component({
  tag: "sdx-menu-flyout-list-item",
  styleUrl: "menu-flyout-list-item.scss",
  shadow: true,
})
export class MenuFlyoutListItem {
  private store: StoreConnection<this, MenuFlyoutState, MenuFlyoutActions>
  private arrowEl?: HTMLElement
  private anchorEl?: HTMLElement

  @Element() public el!: HTMLSdxMenuFlyoutElement

  @State() public state!: StateHandle<MenuFlyoutState>

  @State() private anchorElFocused: boolean = false

  /**
   * If the item is not selectable, it is neither highlighted nor does it have cursor: pointer.
   */
  @Prop() public selectable: boolean = true

  /**
   * The URL this item should link to (if it’s a regular link not handled by JS).
   */
  @Prop() public href: string = "javascript:;"

  /**
   * The icon which is displayed before the text.
   */
  @Prop() public iconName?: IconName

  /**
   * Language of the page the URL points to.
   */
  @Prop() public hreflang?: string

  /**
   * Target of the link ("&#95;blank", "&#95;parent", "&#95;top", "&#95;self", or the name of a window or frame)
   */
  @Prop() public target?: string

  /**
   * Whether the item is disabled.
   */
  @Prop() public disabled: boolean = false

  @Listen("click", { capture: true })
  public onClick(e: MouseEvent) {
    if (this.disabled) {
      e.stopPropagation()
    }
  }

  @Listen("focusin")
  @Listen("focusout")
  public onFocusChange() {
    this.anchorElFocused = !this.anchorElFocused
  }

  @Method()
  public async doFocus() {
    this.anchorEl!.focus()
  }

  @Method()
  public async doClick() {
    this.anchorEl!.click()
  }

  constructor() {
    this.store = new StoreConnection(
      this,
      menuFlyoutReducer,
      getInitialState(),
      ["direction"],
    )
  }

  public connectedCallback() {
    // Re-register arrow el (but only if already rendered)
    if (this.arrowEl) {
      this.store.dispatch({
        type: "ADD_ARROW_EL",
        arrowEl: this.arrowEl,
      })
    }
  }

  public disconnectedCallback() {
    // Unregister arrow el (but only if already rendered)
    // If a component is created and appended in direct succession (like with
    // Angular's ng-content), disconnectedCallback() is called before render()
    if (this.arrowEl) {
      this.store.dispatch({
        type: "REMOVE_ARROW_EL",
        arrowEl: this.arrowEl,
      })
    }
  }

  public componentDidLoad() {
    // Register arrow el
    this.store.dispatch({
      type: "ADD_ARROW_EL",
      arrowEl: this.arrowEl!,
    })

    this.store.subscribe()
  }

  private getHostClassNames() {
    const { direction } = this.state.get()

    return {
      [getAppearance(this.el)]: true,
      selectable: this.selectable && !this.disabled,
      disabled: this.disabled,
      ...computedProperty(direction),
    }
  }

  private getComponentClassNames() {
    return {
      component: true,
      focused: this.anchorElFocused,
    }
  }

  private getLinkClassNames() {
    return {
      body: true,
      disabled: this.disabled,
    }
  }

  public render() {
    return (
      // eslint-disable-next-line jsx-a11y/role-supports-aria-props
      <Host
        class={this.getHostClassNames()}
        aria-disabled={this.disabled && "true"}
        role="listitem"
      >
        <div class={this.getComponentClassNames()}>
          <div class="arrow" ref={(el) => (this.arrowEl = el)}></div>

          <a
            href={this.href}
            hreflang={this.hreflang}
            target={this.target}
            class={this.getLinkClassNames()}
            ref={(el) => (this.anchorEl = el)}
          >
            {this.iconName && (
              <sdx-icon class="icon" iconName={this.iconName} size={2} />
            )}
            <slot />
          </a>
        </div>
      </Host>
    )
  }
}
